/**
 * Manages toast display with delayed appearance and automatic cleanup
 * @param {Function} callback - Async function that receives isCancelled checker
 * @param {Object} options - Configuration object
 * @param {number} options.delay - Delay before showing toast (ms)
 * @param {Object} options.toastConfig - Toast show/remove functions
 * @param {Function} options.onCancel - Optional cancel callback
 */
export const withToastDelay = async (callback, { delay = 500, toastConfig, onCancel }) => {
  let cancelRequested = false;
  let toastShown = false;
  
  const toastTimer = setTimeout(() => {
    toastShown = true;
    toastConfig.show(() => {
      cancelRequested = true;
      onCancel?.();
    });
  }, delay);

  const cleanup = () => {
    if (toastShown) {
      toastConfig.remove();
    } else {
      clearTimeout(toastTimer);
    }
  };

  try {
    await callback(() => cancelRequested);
    cleanup();
  } catch (error) {
    cleanup();
    throw error;
  }
};

/**
 * Downloads response stream with cancellation support
 * @param {Response} response - Fetch API response with body stream
 * @param {Function} shouldCancel - Function that returns true if cancelled
 * @returns {Promise<Blob|null>} Blob if successful, null if cancelled
 */
export const downloadStream = async (response, shouldCancel) => {
  const reader = response.body.getReader();
  const stream = new ReadableStream({
    start(controller) {
      function pump() {
        return reader.read().then(({ done, value }) => {
          if (done || shouldCancel()) {
            controller.close();
            return;
          }
          controller.enqueue(value);
          return pump();
        });
      }
      return pump();
    },
  });

  const blob = await new Response(stream).blob();
  return shouldCancel() ? null : blob;
};

/**
 * Triggers browser file download
 * @param {Blob} blob - File blob to download
 * @param {string} fileName - Name for the downloaded file
 */
export const triggerDownload = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};